import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiUsers, FiArrowLeft, FiSave, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { createCoordinator, getAllCoordinators } from './coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import './CreateCoordinator.css';

export default function CreateCoordinator() {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        phone: '',
        assignedGuilds: [],
    });
    const [guilds, setGuilds] = useState([]);
    const [takenGuildIds, setTakenGuildIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadGuilds();
    }, []);

    async function loadGuilds() {
        try {
            const [guildsData, allCoordinators] = await Promise.all([
                getAllGuilds(),
                getAllCoordinators(),
            ]);
            setGuilds(guildsData);

            // Build set of guild IDs already assigned to any coordinator
            const taken = new Set();
            allCoordinators.forEach(c =>
                (c.assignedGuilds || []).forEach(gid => taken.add(gid))
            );
            setTakenGuildIds(taken);
        } catch (err) {
            console.error('Error loading guilds:', err);
        }
    }

    function handleChange(e) {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError('');
    }

    function handleGuildToggle(guildId) {
        const currentGuilds = [...formData.assignedGuilds];
        const index = currentGuilds.indexOf(guildId);

        if (index > -1) {
            currentGuilds.splice(index, 1);
        } else {
            currentGuilds.push(guildId);
        }

        setFormData({
            ...formData,
            assignedGuilds: currentGuilds,
        });
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!formData.email || !formData.password || !formData.fullName || !formData.phone || formData.assignedGuilds.length === 0) {
            setError('Please fill in all required fields and select at least one guild');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setSuccess(false);
            await createCoordinator(formData);
            setSuccess(true);

            setTimeout(() => {
                navigate('/dashboard/coordinators');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to create coordinator');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>Create Coordinator</h1>
                <button
                    type="button"
                    onClick={() => navigate('/dashboard/coordinators')}
                    className="btn-secondary"
                >
                    <FiArrowLeft />
                    <span>Back to List</span>
                </button>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className="alert alert-error">
                            <FiAlertCircle />
                            <span>{error}</span>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success">
                            <FiCheckCircle />
                            <span>Coordinator created successfully! You can now share the credentials manually. Redirecting...</span>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="fullName">
                            <FiUsers />
                            <span>Full Name *</span>
                        </label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">
                            <FiMail />
                            <span>Email Address *</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="coordinator@example.com"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">
                            <FiUsers />
                            <span>Phone Number *</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+91 98765 43210"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            <FiAlertCircle />
                            <span>Password *</span>
                        </label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Minimum 6 characters"
                            required
                            disabled={loading}
                        />
                        <small className="form-hint">
                            Set a password manually and share it with the coordinator
                        </small>
                    </div>

                    <div className="form-group">
                        <label>
                            <FiUsers />
                            <span>Assigned Guilds *</span>
                        </label>
                        <div className="guilds-selection-grid">
                            {guilds.map((guild) => {
                                const isTaken = takenGuildIds.has(guild.id);
                                return (
                                    <div
                                        key={guild.id}
                                        className={`guild-checkbox-item ${formData.assignedGuilds.includes(guild.id) ? 'selected' : ''} ${isTaken ? 'disabled' : ''}`}
                                        onClick={() => !loading && !isTaken && handleGuildToggle(guild.id)}
                                        title={isTaken ? 'Already assigned to another coordinator' : ''}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formData.assignedGuilds.includes(guild.id)}
                                            onChange={() => { }}
                                            disabled={loading || isTaken}
                                        />
                                        <span>{guild.name}</span>
                                        {isTaken && <span className="taken-badge">Assigned</span>}
                                    </div>
                                );
                            })}
                        </div>
                        {guilds.length === 0 && <p className="empty-guilds-msg">No guilds found. Please create a guild first.</p>}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/coordinators')}
                            className="btn-cancel"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span>Creating...</span>
                                </>
                            ) : (
                                <>
                                    <FiSave />
                                    <span>Create Coordinator</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
